import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import ListingCard from '../../components/ListingCard';
import { Plus, Edit, Trash2, Loader2, Building, Eye } from 'lucide-react';
import { formatCurrency, formatDate, getListingTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=400';

export default function HostListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, listing: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await listingsApi.getHostListings();
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.listing) return;
    setDeleting(true);
    try {
      await listingsApi.delete(deleteDialog.listing.id);
      toast.success('Listing deleted');
      fetchListings();
    } catch (error) {
      toast.error('Failed to delete listing');
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, listing: null });
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-farmhouse-stone/20 text-farmhouse-muted',
      submitted: 'bg-farmhouse-gold/20 text-farmhouse-gold',
      approved: 'bg-farmhouse-moss/20 text-green-400',
      rejected: 'bg-farmhouse-clay/20 text-farmhouse-clay',
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="host-listings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream">
              My Listings
            </h1>
            <p className="text-farmhouse-muted mt-1">
              Manage your properties and availability
            </p>
          </div>
          <Link to="/host/listings/new">
            <Button className="btn-primary" data-testid="new-listing-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Listing
            </Button>
          </Link>
        </div>

        {listings.length > 0 ? (
          <div className="space-y-4">
            {listings.map((listing) => (
              <div
                key={listing.id}
                className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md overflow-hidden"
                data-testid={`listing-row-${listing.id}`}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-48 h-40 md:h-auto relative">
                    <img
                      src={listing.photos?.[0] || defaultImage}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = defaultImage; }}
                    />
                  </div>
                  
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${getStatusBadge(listing.status)} border-0 capitalize`}>
                            {listing.status}
                          </Badge>
                          <Badge variant="outline" className="border-farmhouse-subtle text-farmhouse-muted">
                            {getListingTypeLabel(listing.listing_type)}
                          </Badge>
                        </div>
                        <h3 className="font-serif text-xl font-semibold text-farmhouse-cream">
                          {listing.title}
                        </h3>
                        <p className="text-farmhouse-muted text-sm mt-1">
                          {listing.location?.city}, {listing.location?.state}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-serif font-semibold text-farmhouse-cream">
                          {formatCurrency(listing.pricing?.nightly || 0)}
                        </p>
                        <p className="text-farmhouse-muted text-sm">per night</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mt-4 text-sm text-farmhouse-muted">
                      <span>Capacity: {listing.capacity}</span>
                      <span>•</span>
                      <span>{listing.review_count || 0} reviews</span>
                      <span>•</span>
                      <span>Created {formatDate(listing.created_at)}</span>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/listings/${listing.id}`)}
                        className="border-farmhouse-subtle text-farmhouse-cream hover:bg-farmhouse-subtle"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/host/listings/${listing.id}/edit`)}
                        className="border-farmhouse-subtle text-farmhouse-cream hover:bg-farmhouse-subtle"
                        data-testid="edit-listing"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteDialog({ open: true, listing })}
                        className="border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10"
                        data-testid="delete-listing"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
            <Building className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
            <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
              No listings yet
            </h3>
            <p className="text-farmhouse-muted mb-4">
              Create your first listing to start earning
            </p>
            <Link to="/host/listings/new">
              <Button className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create Listing
              </Button>
            </Link>
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-farmhouse-paper border-farmhouse-subtle">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-farmhouse-cream">Delete Listing?</AlertDialogTitle>
              <AlertDialogDescription className="text-farmhouse-muted">
                This will permanently delete &quot;{deleteDialog.listing?.title}&quot;. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-farmhouse-subtle text-farmhouse-cream border-0 hover:bg-farmhouse-stone">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-farmhouse-clay hover:bg-farmhouse-clay/80 text-white"
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
